import React from 'react';
import { AbsoluteFill, Sequence, useCurrentFrame, useVideoConfig, spring, interpolate, Easing } from 'remotion';

// 确定性随机数生成器（基于索引和帧）
const seededRandom = (seed: number, frame: number): number => {
  const x = Math.sin(seed * 9999 + frame * 1234) * 10000;
  return x - Math.floor(x);
};

// OpenClaw 配色方案
const OPENCLAW_COLORS = {
  background: '#08090c',      // 深黑
  primary: '#f59e0b',         // 暖橙（OpenClaw 主色）
  secondary: '#06b6d4',       // 冷青（科技感）
  earth: '#10b981',           // 地球绿
  white: '#ffffff',
  black: '#000000',
};

// 封面页 - 龙虾吞噬地球
const CoverPage: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // 龙虾从小到大浮现
  const lobsterScale = spring({
    frame,
    fps,
    from: 0,
    to: 1.5,
    config: { damping: 50, stiffness: 200 },
  });

  // 地球旋转
  const earthRotate = interpolate(frame, [0, 150], [0, 360], {
    extrapolateRight: 'clamp',
    easing: Easing.linear,
  });

  // 钳子开合动画
  const clawOpen = Math.sin(frame * 0.2) * 10 + 90;

  return (
    <AbsoluteFill style={{
      justifyContent: 'center',
      alignItems: 'center',
      background: OPENCLAW_COLORS.background,
    }}>
      {/* 背景粒子效果 */}
      <div style={{
        position: 'absolute',
        width: '100%',
        height: '100%',
        background: 'radial-gradient(circle at center, rgba(245,158,11,0.1) 0%, transparent 70%)',
      }} />

      {/* 数据流背景 */}
      {[...Array(20)].map((_, i) => {
        const y = interpolate(
          frame,
          [0, 150],
          [800 + i * 100, -200 - i * 50],
          { extrapolateRight: 'clamp' }
        );

        return (
          <div
            key={i}
            style={{
              position: 'absolute',
              left: `${(i * 5) % 100}%`,
              top: y,
              fontSize: 20,
              color: `rgba(6,182,212,${0.1 + seededRandom(i, frame) * 0.2})`,
              fontFamily: 'monospace',
              opacity: 0.3,
            }}
          >
            {seededRandom(i, frame) > 0.5 ? '01' : '10'}
          </div>
        );
      })}

      {/* 地球 */}
      <div style={{
        position: 'absolute',
        width: 200,
        height: 200,
        borderRadius: '50%',
        background: `linear-gradient(135deg, ${OPENCLAW_COLORS.earth} 0%, ${OPENCLAW_COLORS.secondary} 100%)`,
        transform: `rotate(${earthRotate}deg)`,
        boxShadow: `0 0 60px rgba(16,185,129,0.5)`,
      }}>
        {/* 地球纹理 */}
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            style={{
              position: 'absolute',
              width: 20,
              height: 20,
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.3)',
              left: `${20 + i * 30}%`,
              top: `${20 + (i % 3) * 25}%`,
            }}
          />
        ))}
      </div>

      {/* 龙虾 */}
      <div style={{
        position: 'absolute',
        transform: `scale(${lobsterScale})`,
        zIndex: 10,
      }}>
        <div style={{ fontSize: 150, textAlign: 'center' }}>🦞</div>
      </div>

      {/* 标题 */}
      <div style={{
        position: 'absolute',
        bottom: 150,
        textAlign: 'center',
        opacity: interpolate(frame, [60, 90], [0, 1], { extrapolateRight: 'clamp' }),
      }}>
        <div style={{
          fontSize: 80,
          fontWeight: 'bold',
          color: OPENCLAW_COLORS.white,
          textShadow: `0 0 40px ${OPENCLAW_COLORS.primary}`,
          marginBottom: 20,
        }}>
          OpenClaw 吞噬世界
        </div>
        <div style={{
          fontSize: 40,
          color: OPENCLAW_COLORS.secondary,
        }}>
          The AI Assistant That Devours The World
        </div>
      </div>
    </AbsoluteFill>
  );
};

// 数据吞噬页 - 像素化效果
const DataDevourPage: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const scale = spring({
    frame,
    fps,
    from: 0.8,
    to: 1,
    config: { damping: 100, stiffness: 200 },
  });

  // 像素化动画
  const pixelSize = interpolate(frame, [0, 150], [20, 5], { extrapolateRight: 'clamp' });

  return (
    <AbsoluteFill style={{
      justifyContent: 'center',
      alignItems: 'center',
      background: OPENCLAW_COLORS.background,
    }}>
      {/* 像素化网格 */}
      <div style={{
        position: 'absolute',
        width: '100%',
        height: '100%',
        display: 'grid',
        gridTemplateColumns: `repeat(auto-fill, ${pixelSize}px)`,
        gridTemplateRows: `repeat(auto-fill, ${pixelSize}px)`,
        gap: 2,
        opacity: 0.3,
      }}>
        {[...Array(200)].map((_, i) => (
          <div
            key={i}
            style={{
              width: pixelSize,
              height: pixelSize,
              background: seededRandom(i, frame) > 0.5 ? OPENCLAW_COLORS.primary : OPENCLAW_COLORS.secondary,
              borderRadius: 2,
            }}
          />
        ))}
      </div>

      {/* 中心龙虾 */}
      <div style={{
        transform: `scale(${scale})`,
        textAlign: 'center',
        zIndex: 10,
      }}>
        <div style={{ fontSize: 180, marginBottom: 30 }}>🦞</div>

        <div style={{
          fontSize: 60,
          fontWeight: 'bold',
          color: OPENCLAW_COLORS.white,
          textShadow: `0 0 30px ${OPENCLAW_COLORS.primary}`,
        }}>
          数据吞噬中...
        </div>

        <div style={{
          fontSize: 36,
          color: OPENCLAW_COLORS.secondary,
          marginTop: 20,
        }}>
          {Math.floor(interpolate(frame, [0, 150], [0, 474], { extrapolateRight: 'clamp' }))} / 474 效果已研发
        </div>
      </div>
    </AbsoluteFill>
  );
};

// 品牌露出页
const BrandPage: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const scale = spring({
    frame,
    fps,
    from: 0.8,
    to: 1,
    config: { damping: 100, stiffness: 200 },
  });

  const rotate = interpolate(frame, [0, 90], [0, 360], { extrapolateRight: 'clamp' });

  return (
    <AbsoluteFill style={{
      justifyContent: 'center',
      alignItems: 'center',
      background: `linear-gradient(135deg, ${OPENCLAW_COLORS.primary} 0%, ${OPENCLAW_COLORS.secondary} 100%)`,
    }}>
      <div style={{
        textAlign: 'center',
        transform: `scale(${scale})`,
        zIndex: 10,
      }}>
        {/* 旋转龙虾 */}
        <div style={{
          fontSize: 120,
          marginBottom: 30,
          transform: `rotate(${rotate}deg)`,
        }}>
          🦞
        </div>

        {/* 品牌名 */}
        <div style={{
          fontSize: 70,
          fontWeight: 'bold',
          color: OPENCLAW_COLORS.white,
          marginBottom: 20,
          textShadow: '0 4px 20px rgba(0,0,0,0.3)',
        }}>
          OpenClaw
        </div>

        {/* 标语 */}
        <div style={{
          fontSize: 36,
          color: OPENCLAW_COLORS.white,
          marginBottom: 40,
          opacity: 0.9,
        }}>
          吞噬世界的 AI 助手
        </div>

        {/* 数据 */}
        <div style={{
          fontSize: 28,
          color: OPENCLAW_COLORS.white,
          background: 'rgba(0,0,0,0.3)',
          padding: '15px 30px',
          borderRadius: 20,
          border: '2px solid rgba(255,255,255,0.5)',
        }}>
          474 个视频效果 · 24/7 不停歇 · 自动研发
        </div>
      </div>
    </AbsoluteFill>
  );
};

// 主组件 - 完整时间轴
export const OpenClawDevoursWorld: React.FC = () => {
  return (
    <AbsoluteFill>
      {/* 0-5秒: 封面 - 龙虾吞噬地球 */}
      <Sequence from={0} durationInFrames={150}>
        <CoverPage />
      </Sequence>

      {/* 5-10秒: 数据吞噬 */}
      <Sequence from={150} durationInFrames={150}>
        <DataDevourPage />
      </Sequence>

      {/* 10-15秒: 品牌露出 */}
      <Sequence from={300} durationInFrames={150}>
        <BrandPage />
      </Sequence>
    </AbsoluteFill>
  );
};
